/**
 * AgentsInventoryPageV2 — Phase 3 of the Rebirth migration.
 *
 * The unified Agentlets inventory across all four archetypes
 * (AutoLet / Let / ExteLet / SysLet). Per Design Principles §3.3.1
 * and §6.2, the page composes:
 *
 *   - FacetRail (left): archetype, phase, autonomy, trust band, flags
 *   - Inventory toolbar with current filter chips + clear
 *   - Table (center): name, type, phase, trust, autonomy, connectors, owner, last action
 *   - Slideout (right): triage view with Summary / Audit / Five-Layer Verdict / Phase Gates
 *   - Page header with the archetype-aware "+ New Agentlet" menu
 *
 * Per the §5.3 composition rule, this page renders the v1 AgentsPage
 * verbatim when its flag is OFF — even with the v2 shell ON. The
 * "Open detail page" CTA in the slideout navigates to /agents which
 * picks up the v1 detail UI until Phase 4 ships its replacement.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useActiveScope } from '../hooks/useActiveScope';
import { usePlatform } from '../contexts/PlatformContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthAgentletsInventoryEnabled, isRebirthAgentletDetailEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import AgentsPage from './AgentsPage';
import {
  Button, Pill, TrustChip, Table, FacetRail, Slideout,
  Card, FiveLayerTrace, PhaseGate, EmptyState, Skeleton, KillSwitchButton,
  Modal,
} from '../components/v2';
import NewAgentletMenu from '../components/v2/NewAgentletMenu';
import AutoCreateLauncher from '../components/v2/AutoCreateLauncher';
import GelRegistrationLauncher from '../components/v2/GelRegistrationLauncher';
import AgentCreateSlideout from '../components/v2/AgentCreateSlideout';
/* EXTELET-V3 · Phase 6.1 — manual entry point uses the same wizard
 * Discovery Promote uses. Opens blank when no seedFromDiscovery. */
import { ExteletRegistrationWizard } from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import { trustBand } from '../utils/trustScore';
import { RunTimeScorePanel } from '../components/v2/trust/RunTimeScorePanel';

const ARCHETYPE_LABEL = { AutoLet: 'AutoLet', Let: 'Let', ExteLet: 'ExteLet', SysLet: 'SysLet' };

/* Phase A · Agent Discovery — Source facet vocabulary.
 * Mirrors backend DiscoveredAgent.ENGINE_* constants + Agent.discoverySource
 * possible values. */
const DISCOVERY_SOURCE_LABEL = {
  MANUAL:       'Manual / authored',
  /* The Smart Agent Wizard. Separate from MANUAL because it is a different act with
   * different consequences: the wizard provisions the connection, mints a credential
   * scoped to the one agent and records the person as its accountable owner, none of
   * which a hand-registration does on its own. An operator filtering for "how did this
   * get here" is asking exactly that question. */
  SMART_WIZARD: 'Smart Agent Wizard',
  /* ADR-001 P3 — compile-source labels for sealed AutoLets. Distinct
   * from MANUAL because these are platform-compiled artefacts, not
   * hand-authored. */
  RUNBOOK:      'Auto Create · runbook',
  WORKFLOW:     'Auto Create · workflow',
  AUTOCREATE:   'Auto Create',
  NETWORK:      'Network scan',
  MCP_PROTOCOL: 'MCP protocol',
  CLOUD_SCAN:   'Cloud platform',
  CODE_REPO:    'Code repository',
  IDENTITY:     'Identity forensics',
  BEHAVIORAL:   'Behavioral fingerprint',
  CORRELATION:  'Multi-engine correlation',
  MARKETPLACE:  'Marketplace',
  IMPORT:       'Bulk import',
};

const FACET_GROUPS = [
  {
    key: 'kind', label: 'Inventory',
    options: [
      { key: 'governed', label: 'Protected' },
      { key: 'shadow',   label: 'Shadow (discovered)' },
    ],
  },
  {
    key: 'archetype', label: 'Archetype',
    /* R1.4 — Archetype is a governed-only concept; shadow rows don't
     * carry one. Surfacing this in the rail eliminates the "where are
     * the 576 shadow agents?" confusion. */
    hint: 'Protected agents only',
    options: [
      { key: 'AutoLet', label: 'AutoLet' },
      { key: 'Let',     label: 'Let' },
      { key: 'ExteLet', label: 'ExteLet' },
      { key: 'SysLet',  label: 'SysLet (Platform Admin)', platformAdminOnly: true },
    ],
  },
  {
    key: 'discoverySource', label: 'Source',
    options: [
      { key: 'MANUAL',       label: 'Manual / authored' },
      { key: 'SMART_WIZARD', label: 'Smart Agent Wizard' },
      /* ADR-001 P3 — Auto Create compile sources. */
      { key: 'RUNBOOK',      label: 'Auto Create · runbook' },
      { key: 'WORKFLOW',     label: 'Auto Create · workflow' },
      { key: 'AUTOCREATE',   label: 'Auto Create' },
      { key: 'NETWORK',      label: 'Network scan' },
      { key: 'MCP_PROTOCOL', label: 'MCP protocol' },
      { key: 'CLOUD_SCAN',   label: 'Cloud platform' },
      { key: 'CODE_REPO',    label: 'Code repository' },
      { key: 'IDENTITY',     label: 'Identity forensics' },
      { key: 'BEHAVIORAL',   label: 'Behavioral fingerprint' },
      { key: 'CORRELATION',  label: 'Multi-engine correlation' },
      { key: 'MARKETPLACE',  label: 'Marketplace' },
      { key: 'IMPORT',       label: 'Bulk import' },
    ],
  },
  {
    key: 'riskTier', label: 'Risk tier',
    /* R1.4 — Risk tier is a shadow-only concept and is populated by
     * the discovery engines. Show the hint so operators don't read
     * the zero counts as a bug. */
    hint: 'Shadow agents only',
    options: [
      { key: 'CRITICAL', label: 'Critical' },
      { key: 'HIGH',     label: 'High' },
      { key: 'MEDIUM',   label: 'Medium' },
      { key: 'LOW',      label: 'Low' },
    ],
  },
  {
    key: 'phase', label: 'Phase',
    options: [
      { key: 'Build',  label: 'Build' },
      { key: 'Decide', label: 'Decide' },
      { key: 'Act',    label: 'Act' },
    ],
  },
  {
    key: 'autonomy', label: 'Autonomy',
    options: [
      { key: 'AUTONOMOUS', label: 'Autonomous' },
      { key: 'SUPERVISED', label: 'Supervised' },
      { key: 'RESTRICTED', label: 'Restricted' },
      { key: 'PROBATION',  label: 'Probation' },
    ],
  },
  {
    key: 'trustBand', label: 'Trust band',
    options: [
      { key: 'strong',   label: '≥ 85 (Strong)' },
      { key: 'adequate', label: '70–84 (Adequate)' },
      { key: 'limited',  label: '50–69 (Limited)' },
      { key: 'weak',     label: '30–49 (Weak)' },
      { key: 'critical', label: '< 30 (Critical)' },
      // Agents with no governed evidence yet. They used to band as At risk here, because the
      // local trustBand() started with `Number(score) || 0` — so "never run" read as "failing".
      // They are their own answer, and without this option they could not be filtered for at all.
      { key: 'unrated',  label: 'Unrated (no evidence)' },
    ],
  },
];

/* Until the platform exposes archetype on the agent record, derive it from
 * existing fields (name, type, autoCreated flag). This is a façade that
 * returns 'Let' for typical cases and lets the inventory still work today.
 */
function deriveArchetype(agent) {
  if (agent.archetype) return agent.archetype;
  if (agent.system === true || agent.kind === 'system' || agent.isSystemAgent === true) return 'SysLet';
  /* Fix G54/G55: align with AgentDetailPageV2.deriveArchetype so the
   * inventory pill, facet count, and detail page all agree. Backend
   * marks externals via `isExternal` + `agentClassification:'EXTERNAL'`
   * and AutoLets via `discoverySource`. */
  if (
    agent.external === true ||
    agent.isExternal === true ||
    agent.gel === true ||
    agent.type === 'external' ||
    agent.agentClassification === 'EXTERNAL'
  ) return 'ExteLet';
  /* ADR-001 / GOLDEN_CEG_RULES §1.6 — Authoritative AutoLet signal is
   * `isRunbookManaged=true` on the agent row. This is set at SEAL time
   * by AutoCreate (both runbook-source and workflow-source) and is the
   * canonical "this is a compiled AutoLet" marker. Workflow-source
   * AutoLets keep the source workflow's name (no "AutoLets-" prefix),
   * so the legacy name-prefix check missed them; the isRunbookManaged
   * check catches both. The legacy signals below remain as a defensive
   * fallback for older rows. */
  if (
    agent.isRunbookManaged === true ||
    agent.autoCreated === true ||
    agent.source === 'auto-create' ||
    agent.discoverySource === 'auto-create' ||
    agent.discoverySource === 'autolet' ||
    (typeof agent.name === 'string' && agent.name.startsWith('AutoLets-'))
  ) return 'AutoLet';
  return 'Let';
}

/* ADR-001 P3 — Source label derivation for AutoLet rows. Replaces the
 * "Manual / authored" fallback for runbook-managed agents with the
 * actual compile source (Runbook or Workflow). Pre-existing manually-
 * authored agents continue to render "Manual / authored". */
function deriveDiscoverySource(agent) {
  if (agent.discoverySource && agent.discoverySource !== 'MANUAL') {
    return agent.discoverySource;
  }
  if (agent.isRunbookManaged === true) {
    if (agent.sourceWorkflowId)   return 'WORKFLOW';
    if (agent.runbookDocumentId)  return 'RUNBOOK';
    return 'AUTOCREATE'; // sealed AutoLet, unknown source detail — still not "Manual"
  }
  return agent.discoverySource || 'MANUAL';
}


/* Demo agents used when the PlatformContext doesn't expose any (logged-out
 * dev mode, brand-new tenant). The real fetch path uses ctx.agents. */
const DEMO_AGENTS = [
  { id: 'sophie', name: 'Sophie Cognito', category: 'IAM',        archetype: 'Let',     phase: 'Act', trust: 94, autonomy: 'AUTONOMOUS', connectors: ['Okta','Azure AD'],            owner: 'IAM Platform', last: '14:08', iconLabel: 'SC', iconTone: 'cyan' },
  { id: 'aria',   name: 'Aria Threat',    category: 'Threat',     archetype: 'ExteLet', phase: 'Act', trust: 76, autonomy: 'SUPERVISED', connectors: ['CrowdStrike','Splunk'],       owner: 'SOC',           last: '14:01', iconLabel: 'AT', iconTone: 'brand' },
  { id: 'echo',   name: 'Echo Compliance',category: 'Compliance', archetype: 'Let',     phase: 'Act', trust: 91, autonomy: 'AUTONOMOUS', connectors: ['ServiceNow','Splunk'],        owner: 'GRC',           last: '13:52', iconLabel: 'EC', iconTone: 'success' },
  { id: 'vela',   name: 'Vela AppSec',    category: 'AppSec',     archetype: 'Let',     phase: 'Act', trust: 81, autonomy: 'SUPERVISED', connectors: ['GitHub','Snyk','Jira'],       owner: 'AppSec',        last: '13:44', iconLabel: 'VA', iconTone: 'warning' },
  { id: 'nova',   name: 'Nova IR',        category: 'IR Orch.',   archetype: 'Let',     phase: 'Act', trust: 88, autonomy: 'AUTONOMOUS', connectors: ['ServiceNow','Slack','EDR'],   owner: 'SOC',           last: '13:31', iconLabel: 'NI', iconTone: 'trust' },
  { id: 'atlas',  name: 'Atlas PAM',      category: 'PAM',        archetype: 'Let',     phase: 'Act', trust: 95, autonomy: 'AUTONOMOUS', connectors: ['CyberArk','Vault'],           owner: 'Identity',      last: '13:18', iconLabel: 'AP', iconTone: 'cyan' },
  { id: 'orion',  name: 'Orion Audit',    category: 'Compliance', archetype: 'ExteLet', phase: 'Decide', trust: 72, autonomy: 'RESTRICTED', connectors: ['Splunk','S3'],              owner: 'GRC',           last: '12:54', iconLabel: 'OA', iconTone: 'success' },
  { id: 'autopilot', name: 'AutoPilot Genesis', category: 'Auto-created', archetype: 'AutoLet', phase: 'Build', trust: 0, autonomy: 'PROBATION', connectors: [], owner: 'AutoCreate', last: '12:18', iconLabel: 'AP', iconTone: 'trust' },
];

function AgentsInventoryPageV2Inner() {
  const navigate = useNavigate();
  const location = useLocation();
  /* DEF-FE-OPERATOR-CREATE-GATE-001 — Operator (and any other V208 role
   * without `agent.create` / `agent.delete`) was seeing the New Agentlet
   * dropdown and per-row Delete buttons even though the backend correctly
   * 403s the underlying calls. Pull hasPermission from AuthContext so the
   * FE can hide the buttons consistently with the server gate.
   *
   * SysLet decision — SysLets are platform-managed system agents, restricted
   * to Platform Admin. They are no longer offered in the New-Agentlet create
   * menu (see NewAgentletMenu / agentArchetypes), and the SysLet archetype
   * facet is visible to Platform Admin only. We use the STRICT isPlatformAdmin
   * (NOT isSuperAdmin, which conflates PA + customer-scoped SA). */
  const { session, isPlatformAdmin, hasPermission, loading: authLoading, isAuthenticated } = useAuth();
  /* RC-137a — `useActiveScope()` is the canonical "what scope is the
   * caller currently pinned to?" read. Used here for filter calls only;
   * the create-agent payload (line ~374) still stamps the new agent
   * with session.orgId, which post-RC-137a fix IS the active org for
   * PA (BE /session reads from JWT, like the picker pinned). */
  const activeScope = useActiveScope();
  const canCreateAgent = !!hasPermission && hasPermission('agent.create');
  const canDeleteAgent = !!hasPermission && hasPermission('agent.delete');
  const { agents, loadAgents } = usePlatform();
  const { addToast } = useToast();
  const [selected, setSelected] = useState(null); // selected agent
  const [slideoutTab, setSlideoutTab] = useState('summary');
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsLoaded, setAgentsLoaded] = useState(false);

  /* Fix G45: real Native Agentlet creation modal. Opens whenever the URL
   * carries `?intent=create`. Supports all three usable archetypes
   * (Let / AutoLet / ExteLet) — the form adapts its fields based on
   * which archetype the user picked from the menu (passed via
   * ?archetype=…). SysLet remains gated to Super-Admin server-side. */
  const initialArchetype = (() => {
    const p = new URLSearchParams(location.search);
    return p.get('intent') === 'create' ? (p.get('archetype') || 'let') : null;
  })();
  /* Native Let still uses the in-place modal; AutoLet and ExteLet
   * deep-links open their respective rich launchers. */
  /* Native in-place modal serves the Let create flow. AutoLet/ExteLet
   * deep-links open their dedicated launchers (state below). SysLet is NOT a
   * user-authored archetype (platform-managed system agent), so a
   * ?archetype=syslet deep-link intentionally does NOT open any create flow. */
  const nativeArchetypeDeepLink = initialArchetype === 'let';
  const [createOpen, setCreateOpen] = useState(nativeArchetypeDeepLink);
  const [createArchetype, setCreateArchetype] = useState('let');
  /* G274 — Replace the conflated "Type" dropdown (which mixed execution
   * modes + domain categories) with a clean Execution Mode picker per
   * AGENT_EXECUTION_MODEL.md §1. `executionMode` is the runtime model
   * (cognitive | deterministic | hybrid) backed by the V172 CHECK
   * constraint on `agents.execution_mode`. Default 'deterministic' so a
   * fresh Let mirrors the pre-G260 behaviour until the operator opts in
   * to the cognitive loop or hybrid mode. `type` (domain category) gets
   * a stable default — operators can refine it later on the detail
   * page. Domain tagging is a filtering concern; execution mode is a
   * fundamental runtime decision that belongs in the create flow. */
  /* Five-dimension agent typing model (Phase 1 / V188):
   *   functionMode   — discipline    (compliance / threat / iam / appsec / devops / secops / general)
   *   industryMode   — vertical      (financial / insurance / healthcare / ... / general)
   *   operationMode  — authority     (platform / human / autonomous — autonomous requires Full trust)
   *   executionMode  — reasoning     (deterministic / cognitive / hybrid)
   *   trustBand      — earned        (platform-written via score waterfall, not user-pickable)
   * Cross-mode invariants enforced by V188 CHECK constraints:
   *   operation_mode='human'      ⟹ trust_band IN ('Supervised','Restricted','Probation')
   *   operation_mode='autonomous' ⟹ trust_band = 'Full'
   *   trust_band='Probation'      ⟹ operation_mode = 'human'
   * Fresh agents start at trust_band='Supervised', so operation_mode='autonomous'
   * is effectively unselectable at creation — the inline advisory in the modal
   * warns the operator before the backend rejects with a named-CHECK error. */
  const [createForm, setCreateForm] = useState({
    name: '', description: '', gatewayUrl: '', endpointUrl: '', apiKey: '',
    functionMode:  'general',
    industryMode:  'general',
    operationMode: 'platform',
    executionMode: 'deterministic',
    // Creation-time accountability choice (default: creator owns it and accepts on create).
    accountability: { ownerMode: 'self', ownerPrincipalId: null, accepted: true },
  });
  const [creating, setCreating] = useState(false);
  /* Server-side create drafts (V331). currentDraftId ties the open form to a saved
   * draft so Save updates it in place and a successful create can delete it. */
  const [savingDraft, setSavingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [draftsOpen, setDraftsOpen] = useState(false);
  /* When resuming an ExteLet/AutoLet draft from the central panel, seed the
   * respective wizard from the server draft ({id, form:<snapshot>}). */
  const [exteletSeedDraft, setExteletSeedDraft] = useState(null);
  const [autoletSeedDraft, setAutoletSeedDraft] = useState(null);

  /* Phase 4 follow-up: AutoLet + ExteLet now route to dedicated v2
   * launchers that mirror the v1 multi-stage flows. Native Let stays
   * on the lightweight in-place modal — the name+gateway form is
   * genuinely sufficient for native authoring (the lifecycle stages
   * happen on the detail page after creation). */
  /* Fix: AutoLet / ExteLet deep-links (e.g. Outcomes "+ New Agentlet →
   * Auto-create…" lands here as ?archetype=autolet&intent=create) must
   * auto-open the matching launcher on mount — previously only the
   * native Let path honored the deep-link, so AutoLet just dropped the
   * user on the inventory with nothing open. Mirrors `createOpen` init. */
  const [autoLetOpen, setAutoLetOpen] = useState(initialArchetype === 'autolet');
  const [exteLetOpen, setExteLetOpen] = useState(initialArchetype === 'extelet');
  /* G87 — make the Density toggle real. Cycles comfortable ↔ compact
   * (Table component supports those two). Persisted to localStorage so
   * the user's choice survives reloads. */
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('agentlets-density') || 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const cycleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    try { localStorage.setItem('agentlets-density', next); } catch {}
  };
  /* G87 — Delete from inventory row action. Mirrors AgentDetailPageV2 G85. */
  const [deleteTarget, setDeleteTarget] = useState(null); // agent to confirm-delete
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const openCreate = (archetype) => {
    if (archetype === 'autolet') {
      setAutoLetOpen(true);
      return;
    }
    if (archetype === 'extelet') {
      setExteLetOpen(true);
      return;
    }
    setCreateArchetype(archetype);
    setCreateForm({
      name: '', description: '', gatewayUrl: '', endpointUrl: '', apiKey: '',
      functionMode:  'general',
      industryMode:  'general',
      operationMode: 'platform',
      executionMode: 'deterministic',
      accountability: { ownerMode: 'self', ownerPrincipalId: null, accepted: true },
    });
    setCurrentDraftId(null); // fresh form — not tied to a saved draft yet
    setCreateOpen(true);
    // Reflect in URL so refreshes preserve intent
    navigate(`/agents?archetype=${archetype}&intent=create`, { replace: true });
  };
  const closeCreate = () => {
    setCreateOpen(false);
    navigate('/agents', { replace: true });
  };
  const submitCreate = async () => {
    if (!createForm.name.trim()) {
      addToast({ tone: 'warning', title: 'Name required', description: 'Give your Agentlet a memorable name (e.g. "IAM Sentry").' });
      return;
    }
    if (createArchetype === 'extelet' && !createForm.endpointUrl.trim()) {
      addToast({ tone: 'warning', title: 'Endpoint URL required', description: 'External agents need a reachable endpoint to register against.' });
      return;
    }
    setCreating(true);
    try {
      const create = api['createAgent'];
      if (typeof create !== 'function') throw new Error('createAgent not available');
      const payload = {
        name: createForm.name.trim(),
        /* Phase 1 / V188 — five-dimension agent typing. The old single
         * `type` field that lumped domain + execution + operation
         * together is gone. Backend accepts the four new fields,
         * with trust_band defaulted to 'Supervised' on creation by
         * the score waterfall (NOT user-pickable here). */
        functionMode:  createForm.functionMode  || 'general',
        industryMode:  createForm.industryMode  || 'general',
        operationMode: createForm.operationMode || 'platform',
        executionMode: createForm.executionMode || 'deterministic',
        phase: 'build',
        status: 'active',
        orgId: session?.orgId,
        tenantId: session?.tenantId,
        ...(createForm.gatewayUrl?.trim() ? { gatewayUrl: createForm.gatewayUrl.trim() } : {}),
        ...(createForm.endpointUrl?.trim() ? { endpointUrl: createForm.endpointUrl.trim() } : {}),
        ...(createForm.description?.trim() ? { description: createForm.description.trim() } : {}),
        ...(createArchetype === 'extelet' ? { isExternal: true, agentClassification: 'EXTERNAL' } : {}),
        ...(createArchetype === 'autolet' ? { autoCreated: true, autonomyLevel: 'PROBATION' } : {}),
        // DEF-RBAC-SYSLET-001 — mark system-managed agents so the
        // structural permission (agent.structural) on subsequent
        // edits/deletes properly fires. AgentController already enforces
        // the elevated permission for isSystemAgent edits in
        // update/promote/externalize endpoints; this just stamps the
        // flag at creation time.
        ...(createArchetype === 'syslet' ? { isSystemAgent: true, agentClassification: 'SYSTEM' } : {}),
        // Accountable-owner choice from the shared AccountabilitySection (SysLet is exempt).
        ...(createArchetype !== 'syslet' && createForm.accountability ? { accountability: createForm.accountability } : {}),
      };
      const res = await create(payload);
      if (res?.ok && res.data?.id) {
        /* Fix G47: when the backend silently drops a field we sent
         * (description / endpointUrl / apiKey / agentClassification —
         * these aren't accepted by the current Spring Boot validator),
         * surface a follow-up toast so the user isn't left wondering
         * why the field is empty on the detail page. The audit trail
         * still records the original payload. */
        const droppedFields = [];
        const data = res.data;
        if (createForm.description.trim() && !data.description) droppedFields.push('description');
        if (createForm.endpointUrl.trim() && !data.endpointUrl) droppedFields.push('endpoint URL');
        if (createForm.apiKey && !data.apiKey) droppedFields.push('API key');
        if (createArchetype === 'extelet' && !data.isExternal) droppedFields.push('external classification');
        // DEF-RBAC-SYSLET-001 — include 'syslet' in the archetype label map
        // so the success toast reads "SysLet now in Build phase" instead of
        // falling back to the ExteLet label.
        addToast({ tone: 'success', title: `${createForm.name} created`, description: `${ARCHETYPE_LABEL[createArchetype === 'let' ? 'Let' : createArchetype === 'autolet' ? 'AutoLet' : createArchetype === 'syslet' ? 'SysLet' : 'ExteLet'] || 'Agentlet'} now in Build phase. Add skills next.` });
        if (droppedFields.length > 0) {
          /* G87 — production copy. Tell the user what wasn't saved without
           * exposing internal architecture. They can re-enter on the
           * detail page once they land. */
          setTimeout(() => addToast({
            tone: 'warning',
            title: 'Some fields weren’t saved',
            description: `${droppedFields.join(', ')} ${droppedFields.length > 1 ? 'weren’t' : 'wasn’t'} stored on this Agentlet. Open it and add ${droppedFields.length > 1 ? 'them' : 'it'} from the detail page.`,
          }), 600);
        }
        // The agent is now the source of truth — drop the draft (if we resumed/saved one).
        if (currentDraftId && typeof api.deleteAgentDraft === 'function') {
          try { await api.deleteAgentDraft(currentDraftId); } catch (_e) { /* non-fatal */ }
          setCurrentDraftId(null);
        }
        closeCreate();
        if (typeof loadAgents === 'function') loadAgents({ orgId: activeScope.orgId, tenantId: activeScope.tenantId });
        // Phase A — also refresh the unified inventory used by this page
        reloadInventory();
        /* Fix G54/G55: pass `?archetype=…` on the navigation so the
         * detail page can render the correct archetype pill even before
         * the backend round-trips classification fields. */
        if (isRebirthAgentletDetailEnabled()) navigate(`/agents/${res.data.id}?archetype=${createArchetype}`);
      } else {
        addToast({ tone: 'danger', title: 'Create failed', description: res?.error || 'The platform couldn’t create this Agentlet. Try again or contact your administrator.' });
      }
    } catch (err) {
      addToast({ tone: 'danger', title: 'Create failed', description: err?.message || String(err) });
    } finally {
      setCreating(false);
    }
  };

  /* ── Server-side drafts (V331) — Save / Resume / Discard the create form ────── */
  const saveDraft = async () => {
    if (typeof api.saveAgentDraft !== 'function') {
      addToast({ tone: 'warning', title: 'Drafts unavailable', description: 'Saving drafts isn’t wired in this build.' });
      return;
    }
    setSavingDraft(true);
    try {
      const res = await api.saveAgentDraft({
        id: currentDraftId || undefined,
        archetype: createArchetype,
        name: createForm.name?.trim() || null,
        form: createForm,
      });
      if (res?.ok && res.data?.id) {
        setCurrentDraftId(res.data.id);
        addToast({ tone: 'success', title: 'Draft saved', description: 'Resume it anytime from “Drafts”.' });
      } else {
        addToast({ tone: 'danger', title: 'Couldn’t save draft', description: res?.error || 'Try again.' });
      }
    } catch (err) {
      addToast({ tone: 'danger', title: 'Couldn’t save draft', description: err?.message || String(err) });
    } finally {
      setSavingDraft(false);
    }
  };

  const openDrafts = async () => {
    if (typeof api.listAgentDrafts === 'function') {
      try {
        const res = await api.listAgentDrafts();
        if (res?.ok && Array.isArray(res.data)) setDrafts(res.data);
      } catch (_e) { /* non-fatal — panel still opens, possibly empty */ }
    }
    setDraftsOpen(true);
  };

  const resumeDraft = (draft) => {
    if (!draft) return;
    const arch = draft.archetype || 'let';
    setDraftsOpen(false);
    /* ExteLet + AutoLet resume in their own wizards, seeded from the server draft
     * ({id, form:<snapshot>}); the wizard's useWizardDraft adopts the id so further
     * auto-saves upsert the same row. Native let/syslet use the in-place slideout. */
    if (arch === 'extelet') {
      setExteletSeedDraft(draft);
      setExteLetOpen(true);
      navigate('/agents?archetype=extelet&intent=create', { replace: true });
      return;
    }
    if (arch === 'autolet') {
      setAutoletSeedDraft(draft);
      setAutoLetOpen(true);
      navigate('/agents?archetype=autolet&intent=create', { replace: true });
      return;
    }
    setCreateArchetype(arch);
    setCreateForm({
      name: '', description: '', gatewayUrl: '', endpointUrl: '', apiKey: '',
      functionMode: 'general', industryMode: 'general', operationMode: 'platform', executionMode: 'deterministic',
      ...(draft.form && typeof draft.form === 'object' ? draft.form : {}),
    });
    setCurrentDraftId(draft.id);
    setCreateOpen(true);
    navigate(`/agents?archetype=${arch}&intent=create`, { replace: true });
  };

  const discardDraft = async (id) => {
    if (typeof api.deleteAgentDraft !== 'function') return;
    try {
      await api.deleteAgentDraft(id);
      setDrafts((d) => d.filter((x) => x.id !== id));
      if (currentDraftId === id) setCurrentDraftId(null);
      addToast({ tone: 'success', title: 'Draft discarded' });
    } catch (err) {
      addToast({ tone: 'danger', title: 'Couldn’t discard draft', description: err?.message || String(err) });
    }
  };

  /* G87 — delete confirmation handler. Backend enforces the actual
   * `has_children` rule (returns 409); we surface a plain-language
   * message + reload the inventory on success. */
  const onDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const del = api['deleteAgent'];
      if (typeof del !== 'function') throw new Error('Delete isn’t available in this build.');
      const res = await del(deleteTarget.id);
      if (res?.ok) {
        addToast({ tone: 'success', title: `${deleteTarget.name} deleted`, description: 'A record of this deletion is preserved in the audit trail.' });
        setDeleteTarget(null);
        if (typeof loadAgents === 'function') loadAgents({ orgId: activeScope.orgId, tenantId: activeScope.tenantId });
        // Phase A — also refresh the unified inventory used by this page
        reloadInventory();
        return;
      }
      const msg = res?.error || res?.message || '';
      if (/has_children|child|409/i.test(msg)) {
        setDeleteError('This Agentlet has newer versions that must be deleted first. Open the latest version, delete it, then come back here.');
      } else {
        setDeleteError(msg || 'The platform couldn’t delete this Agentlet.');
      }
    } catch (err) {
      setDeleteError(err?.message || String(err));
    } finally {
      setDeleting(false);
    }
  };

  /* Phase A · Agent Discovery — unified inventory data source.
   *
   * The page used to call PlatformContext.loadAgents() which hits
   * GET /api/v1/agents (governed only). We now drive the page off the
   * new GET /api/v1/agents/inventory endpoint which mixes in shadow-
   * discovered rows tagged kind="shadow" alongside governed ones tagged
   * kind="governed". Existing PlatformContext.agents is left untouched
   * for other consumers (slideouts, dashboards) that don't yet need
   * the unified view — no regression. */
  const [inventoryRows, setInventoryRows] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState(null);

  useEffect(() => {
    // Wait until the auth session has rehydrated before fetching. On a hard
    // refresh `session` is null for the first render(s); firing the fetch then
    // sent an UNSCOPED request and re-fired once scope resolved — a wasteful
    // double-fetch and a flash risk. Gate on !authLoading so we issue exactly
    // one scoped request. (/inventory is userId-governed server-side, so this
    // is correctness/UX, not a cross-tenant leak — that was the /agents
    // endpoint, fixed separately.)
    if (authLoading) return;
    let cancelled = false;
    setInventoryLoading(true);
    setInventoryError(null);
    api.getAgentInventory({ orgId: activeScope.orgId, tenantId: activeScope.tenantId, includeShadow: true })
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res?.data) ? res.data
                    : Array.isArray(res) ? res
                    : [];
        setInventoryRows(data);
        setAgentsLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setInventoryError(err?.message || String(err));
        addToast({
          tone: 'danger',
          title: 'Failed to load Agentlets',
          description: err?.message || String(err),
        });
        setAgentsLoaded(true);
      })
      .finally(() => { if (!cancelled) setInventoryLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [authLoading, activeScope.orgId, activeScope.tenantId]);

  // Reload helper used after create/delete actions
  const reloadInventory = () => {
    setInventoryLoading(true);
    api.getAgentInventory({ orgId: activeScope.orgId, tenantId: activeScope.tenantId, includeShadow: true })
      .then((res) => {
        const data = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setInventoryRows(data);
      })
      .catch((err) => {
        addToast({ tone: 'danger', title: 'Reload failed', description: err?.message || String(err) });
      })
      .finally(() => setInventoryLoading(false));
  };

  /* Phase A · Faceted filter state — added `kind`, `discoverySource`,
   * `riskTier` to support the unified inventory facets. Existing
   * archetype/phase/autonomy/trustBand stay untouched.
   *
   * R3.1 — Honor `?type=ExteLet|Let|AutoLet|SysLet` on first mount so
   * the sidebar EXTELETS → Registrations link can deep-link straight
   * into a pre-filtered inventory. The facet stays mutable from there;
   * the URL is only consulted on initial mount (subsequent in-page
   * toggles don't rewrite the URL — keeps shareable links sticky but
   * doesn't fight the user). */
  const ALLOWED_TYPES = ['AutoLet', 'Let', 'ExteLet', 'SysLet'];
  const parseTypeParam = (search) => {
    try {
      const t = new URLSearchParams(search).get('type');
      return ALLOWED_TYPES.includes(t) ? t : null;
    } catch { return null; }
  };
  const initialArchetypeFromUrl = parseTypeParam(location.search);
  /* Derived on each render so the scope pill disappears immediately
   * when the user clicks "clear" (which navigates back to /agents). */
  const currentArchetypeFromUrl = parseTypeParam(location.search);
  const [facets, setFacets] = useState({
    kind:            new Set(),
    archetype:       initialArchetypeFromUrl ? new Set([initialArchetypeFromUrl]) : new Set(),
    discoverySource: new Set(),
    riskTier:        new Set(),
    phase:           new Set(),
    autonomy:        new Set(),
    trustBand:       new Set(),
  });

  const toggleFacet = (groupKey, optionKey, nextChecked) => {
    setFacets((prev) => {
      const next = new Set(prev[groupKey] || []);
      if (nextChecked) next.add(optionKey); else next.delete(optionKey);
      return { ...prev, [groupKey]: next };
    });
  };

  const clearFacets = () => setFacets({
    kind: new Set(), archetype: new Set(), discoverySource: new Set(), riskTier: new Set(),
    phase: new Set(), autonomy: new Set(), trustBand: new Set(),
  });

  const allAgents = useMemo(() => {
    /* Phase A · Agent Discovery — render InventoryRowDto from the new
     * /api/v1/agents/inventory endpoint. Each row carries `kind`
     * ("governed" | "shadow") + `discoverySource` so the page can split
     * presentation per kind without two separate queries. Shadow rows
     * lack phase/trust/autonomy by definition; we render a placeholder
     * row that still surfaces the SHADOW pill, source, and risk tier
     * for triage. */
    const rows = Array.isArray(inventoryRows) ? inventoryRows : [];
    if (rows.length > 0) {
      const titleCase = (s) => s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).toLowerCase() : '';
      return rows.map((r) => {
        const isShadow = r.kind === 'shadow';
        return {
          id: r.id,
          kind: r.kind || 'governed',
          name: r.name || r.displayName || r.id,
          category: r.agentType || r.category || r.type || '—',
          archetype: isShadow ? null : deriveArchetype(r),
          /* Shadow rows have no phase; show "Shadow" so the column doesn't
           * render an undefined Pill. Governed rows keep the existing
           * lowercase→titleCase normalization. */
          phase: isShadow ? 'Shadow' : titleCase(r.phase || r.lifecyclePhase || 'Act'),
          /* DEF-DISC-TRUST-100-001 — Trust column MUST render trustScore,
           * not complianceScore. The previous fallback chain
           * (complianceScore ?? trustScore) was masking the real trust
           * value: complianceScore defaults to 100 at agent creation
           * (no violations yet) while trustScore for a fresh ExteLet is 50.
           * Operators were reading "100" as full trust on shadow-promoted
           * agents that hadn't earned a single successful invocation yet.
           *
           * Shadow rows still use discoveryConfidence (the detection
           * confidence is the closest analogue to trust before promotion).
           * Governed rows use the CANONICAL complianceScore (null = Unrated) —
           * NOT the trustScore baseline (75/50) which fabricated a posture for
           * never-run agents and diverged from the detail/Trust-Center reads.
           * null flows through to TrustChip which renders "Unrated".
           * See docs/design/TRUST_SCORE_SINGLE_SOURCE_RC.md. */
          trust: isShadow ? (r.discoveryConfidence ?? 0)
                          : (r.complianceScore ?? null),
          autonomy: isShadow ? '—' : (r.autonomy || r.autonomyLevel || 'SUPERVISED'),
          connectors: (() => {
            const raw = r.connectors || r.assignedConnectorIds || r.connectorIds || [];
            if (!Array.isArray(raw)) return [];
            return raw.map(c => typeof c === 'string' ? c : (c?.name || c?.connectorName || c?.id || '')).filter(Boolean);
          })(),
          owner: r.owner || r.ownerName || r.team || (isShadow ? r.networkIdentifier || '—' : '—'),
          last: r.lastExecutionAt
            ? new Date(r.lastExecutionAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : (r.lastHeartbeat
                ? new Date(r.lastHeartbeat).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                : (r.lastSeenAt
                    ? new Date(r.lastSeenAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : '—')),
          /* Phase A — new fields surfaced uniformly on every row.
           * ADR-001 P3 — runbook-managed agents derive source from
           * lineage fields (sourceWorkflowId / runbookDocumentId) so the
           * facet rail counts and the row label both reflect that the
           * agent was compiled, not hand-authored. */
          discoverySource: isShadow
            ? (r.sourceEngine || r.discoverySource || 'CORRELATION')
            : deriveDiscoverySource(r),
          discoveryConfidence: r.discoveryConfidence,
          riskTier: r.riskTier,                                  // shadow-only
          networkIdentifier: r.networkIdentifier,                 // shadow-only
          agentClassification: r.agentClassification,
          iconLabel: (r.name || 'A').slice(0, 2).toUpperCase(),
          iconTone: isShadow ? 'amber' : 'cyan',
          raw: r,
        };
      });
    }
    /* Demo rows are a LOGGED-OUT / dev-shell placeholder only. Previously
     * this returned DEMO_AGENTS whenever `!agentsLoaded`, which made a hard
     * refresh flash 7 fake agents (Sophie Cognito, Atlas PAM, …) before the
     * real, scoped inventory arrived — the "wrong set" the operator saw. For
     * an authenticated session we render nothing here and let the loading
     * skeleton show instead (see render below). */
    if (agentsLoaded && !isAuthenticated) {
      return DEMO_AGENTS.map((a) => ({ ...a, raw: a, kind: 'governed' }));
    }
    return [];
  }, [inventoryRows, agentsLoaded, isAuthenticated]);

  const filtered = useMemo(() => {
    return allAgents.filter((a) => {
      if (facets.kind.size > 0 && !facets.kind.has(a.kind)) return false;
      /* R1.4 — Shadow rows do not belong to any archetype (they aren't
       * governed yet). Previously the archetype filter exempted them,
       * which caused "Filter by AutoLet" to keep ALL 574 shadow rows
       * visible and made the table look like it was leaking ExteLets
       * into the AutoLet view. Now: any archetype selection excludes
       * shadow rows. Operators who want shadow rows can use the
       * "Shadow (discovered)" Inventory facet directly. */
      if (facets.archetype.size > 0
          && (a.kind === 'shadow' || !facets.archetype.has(a.archetype))) return false;
      if (facets.discoverySource.size > 0
          && !facets.discoverySource.has(a.discoverySource)) return false;
      if (facets.riskTier.size > 0 && !facets.riskTier.has(a.riskTier)) return false;
      /* R1.4 — Phase / Autonomy / Trust band are governed-only concepts;
       * shadow rows have no value for them. Previously shadow rows
       * passed-through these filters unconditionally, which let them
       * appear under e.g. "Phase: Build" even though they have no
       * phase. Same root cause as the archetype leak: exclude shadow
       * rows when any of these facets are active. */
      if (facets.phase.size > 0
          && (a.kind === 'shadow' || !facets.phase.has(a.phase))) return false;
      if (facets.autonomy.size > 0
          && (a.kind === 'shadow' || !facets.autonomy.has(a.autonomy))) return false;
      if (facets.trustBand.size > 0
          && (a.kind === 'shadow' || !facets.trustBand.has(trustBand(a.trust)))) return false;
      return true;
    });
  }, [allAgents, facets]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  /* Compute facet counts AFTER applying every OTHER facet — so the user
   * always sees the count they'd get if they toggled this option, while
   * other facets stay applied. (Standard inventory UX.) */
  const facetCounts = useMemo(() => {
    const counts = {};
    for (const group of FACET_GROUPS) {
      counts[group.key] = {};
      for (const opt of group.options) {
        const subset = allAgents.filter((a) => {
          for (const g of FACET_GROUPS) {
            if (g.key === group.key) continue;
            const sel = facets[g.key];
            if (sel.size === 0) continue;
            if (g.key === 'trustBand') {
              if (!sel.has(trustBand(a.trust))) return false;
            } else if (!sel.has(a[g.key])) return false;
          }
          if (group.key === 'trustBand') return trustBand(a.trust) === opt.key;
          return a[group.key] === opt.key;
        });
        counts[group.key][opt.key] = subset.length;
      }
    }
    return counts;
  }, [allAgents, facets]);

  /* R1.4 — Risk tier options are populated by the discovery engines; in
   * practice nearly every shadow row lands in a single bucket (LOW for
   * code-repo sweeps). Showing four lines of "Critical 0 / High 0 /
   * Medium 0 / Low 574" looks like broken math. Hide zero-count tier
   * options unless the user has them actively selected; same for any
   * other facet group where ALL options would be zero except this is
   * scoped to riskTier so trust-band / phase still always render their
   * standard ladder of choices for triage. */
  const HIDE_ZERO_GROUPS = new Set(['riskTier']);
  const isPA = typeof isPlatformAdmin === 'function' ? isPlatformAdmin() : !!isPlatformAdmin;
  const facetGroupsWithCounts = FACET_GROUPS.map((g) => {
    const opts = g.options
      /* SysLet facet is Platform-Admin-only — platform-managed system agents
       * aren't surfaced to customer-scoped Super Admins or below. */
      .filter((o) => !o.platformAdminOnly || isPA)
      .map((o) => ({
        ...o,
        count: facetCounts[g.key]?.[o.key] ?? 0,
        checked: facets[g.key]?.has(o.key) || false,
      }));
    const filtered = HIDE_ZERO_GROUPS.has(g.key)
      ? opts.filter((o) => o.count > 0 || o.checked)
      : opts;
    return { ...g, options: filtered };
  /* R1.4 — Drop facet groups whose entire option set was filtered out
   * (e.g. risk tier when there are no shadow rows yet). Without this,
   * the rail renders an empty fieldset with just the legend dangling. */
  }).filter((g) => g.options.length > 0);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key]) {
        const opt = g.options.find((o) => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets]);

  const openSlideout = (row) => {
    /* DEF-DISC-SHADOW-FILTER-DEADEND-001 — shadow rows MUST deep-link
     * into the Discovery hub's Agents tab WITH the row id preserved so
     * the operator lands on that row's slideout for triage. The legacy
     * /shadow-discovery route is now /discovery?tab=agents; using ?selected=
     * matches the pattern ShadowDiscoveryPageV2 already honors when it
     * mounts (auto-opens the matching row's slideout). Governed rows
     * keep the existing slideout behavior. */
    if (row?.kind === 'shadow') {
      navigate(`/discovery?tab=agents&selected=${row.id}`);
      return;
    }
    setSelected(row);
    setSlideoutTab('summary');
  };
  const closeSlideout = () => setSelected(null);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0 flex items-center gap-2">
            Agentlets
            {/* R3.1 — Surface the typed-deep-link context. When the
             * sidebar EXTELETS → Registrations link landed the user here
             * with ?type=ExteLet, show a dismissable scope pill so the
             * filtered view doesn't feel like a glitch. The pill clears
             * BOTH the archetype facet AND the URL param. */}
            {currentArchetypeFromUrl && (
              <span className="inline-flex items-center gap-1.5">
                <Pill tone={currentArchetypeFromUrl === 'ExteLet' ? 'trust' : 'muted'} size="sm">
                  Scope: {currentArchetypeFromUrl}s
                </Pill>
                <button
                  type="button"
                  className="text-[11px] text-v2-ink-muted hover:text-v2-ink underline-offset-2 hover:underline"
                  onClick={() => {
                    setFacets((prev) => ({ ...prev, archetype: new Set() }));
                    navigate('/agents', { replace: true });
                  }}
                  title="Clear scope and show all archetypes"
                >
                  clear
                </button>
              </span>
            )}
          </h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            {(authLoading || !agentsLoaded || agentsLoading || inventoryLoading) ? 'Loading…' : (() => {
              /* Phase A · Agent Discovery — distinguish governed from shadow
               * in the header so the operator sees "the platform is finding
               * agents on your behalf" at a glance. */
              const governed = allAgents.filter(a => a.kind === 'governed').length;
              const shadow = allAgents.filter(a => a.kind === 'shadow').length;
              const teams = new Set(
                allAgents.filter(a => a.kind === 'governed')
                         .map(a => a.owner).filter(o => o && o !== '—'));
              const teamCount = teams.size;
              const teamLabel = teamCount === 1 ? 'team' : 'teams';
              const teamFragment = teamCount > 0 ? ` across ${teamCount} ${teamLabel}` : '';
              const shadowFragment = shadow > 0
                ? ` · ${shadow} shadow-discovered awaiting triage`
                : '';
              return `${governed} governed${teamFragment}${shadowFragment} · ${filtered.length} match current filters`;
            })()}
            {!agentsLoading && !inventoryLoading && agentsLoaded
              && (Array.isArray(inventoryRows) ? inventoryRows.length === 0 : true)
              && allAgents.length > 0 && (
              <span className="ml-2 text-v2-warning">· showing sample data</span>
            )}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* G87 — replaced non-functional "Saved views" stub with a real
            * Refresh action; the Density button now actually toggles row
            * density and persists the choice. */}
          <Button
            variant="ghost"
            onClick={() => reloadInventory()}
            title="Reload Agentlets from the platform"
          >
            Refresh
          </Button>
          <Button
            variant="secondary"
            onClick={cycleDensity}
            title={`Row density: ${density}. Click to cycle.`}
          >
            Density: {density.charAt(0).toUpperCase() + density.slice(1)}
          </Button>
          {canCreateAgent && (
            <Button variant="secondary" onClick={openDrafts} title="Resume a saved Agentlet-creation draft">
              Drafts
            </Button>
          )}
          {canCreateAgent && <NewAgentletMenu onPick={openCreate} />}
        </div>
      </header>

      {/* Filter chip strip */}
      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button
              key={`${c.groupKey}-${c.optionKey}`}
              type="button"
              onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="v2-focus-ring inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white transition-colors duration-v2"
            >
              {c.label} ×
            </button>
          ))}
          <button
            type="button"
            onClick={clearFacets}
            className="ml-auto v2-focus-ring text-[11.5px] text-v2-ink-muted hover:text-v2-ink"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail
          groups={facetGroupsWithCounts}
          onToggle={toggleFacet}
          ariaLabel="Agentlet filters"
        />

        {(authLoading || !agentsLoaded) ? (
          <div className="space-y-2" aria-busy="true" aria-label="Loading Agentlets">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<AlertTriangle size={20} />}
            title="No Agentlets match"
            description={activeFilterChips.length > 0
              ? 'Try clearing facets or expanding the trust band.'
              : 'Your tenant has no Agentlets yet. Create your first one to get started.'}
            primaryAction={
              activeFilterChips.length > 0
                ? <Button variant="primary" onClick={clearFacets}>Clear filters</Button>
                : (canCreateAgent ? <NewAgentletMenu onPick={openCreate} /> : null)
            }
          />
        ) : (
          <Table
            ariaLabel="Agentlet inventory"
            columns={[
              { key: 'name', header: 'Agentlet', sortable: true, render: (r) => (
                <div className="flex items-center gap-3">
                  <div className={'w-7 h-7 rounded-v2-sm flex items-center justify-center font-bold text-[10px] ' + (
                    r.iconTone === 'cyan' ? 'bg-v2-cyan-soft text-v2-cyan' :
                    r.iconTone === 'brand' ? 'bg-v2-brand-soft text-v2-brand-strong' :
                    r.iconTone === 'success' ? 'bg-v2-success-soft text-v2-success' :
                    r.iconTone === 'warning' ? 'bg-v2-warning-soft text-v2-warning' :
                    r.iconTone === 'trust' ? 'bg-v2-trust-soft text-v2-trust' :
                    'bg-v2-muted text-v2-ink-secondary'
                  )}>{r.iconLabel}</div>
                  <div className="min-w-0 flex-1">
                    {/* R1.2 / R1.4 — long shadow-discovered names blew out
                     * the column and pushed the per-row Canvas action off
                     * the right edge. Tightened max-width from 440 to 260
                     * so all 7 columns + the right-aligned action button
                     * stay on-screen at the 1500px page width. The full
                     * name is still available on hover and on the detail
                     * page. */}
                    <div className="font-semibold text-v2-ink truncate max-w-[260px]" title={r.name}>{r.name}</div>
                    {/* R1.4 — Subtitle previously rendered "— · —" when both
                     * category and owner were unknown (most shadow rows).
                     * That looked like garbled punctuation. Suppress when
                     * neither side has real content. */}
                    {((r.category && r.category !== '—') || (r.owner && r.owner !== '—')) && (
                      <div className="text-[10.5px] text-v2-ink-muted truncate max-w-[260px]" title={`${r.category} · ${r.owner}`}>
                        {r.category && r.category !== '—' ? r.category : ''}
                        {(r.category && r.category !== '—' && r.owner && r.owner !== '—') ? ' · ' : ''}
                        {r.owner && r.owner !== '—' ? r.owner : ''}
                      </div>
                    )}
                  </div>
                </div>
              )},
              { key: 'archetype', header: 'Type', render: (r) => (
                /* Phase A · Agent Discovery — shadow rows have no archetype
                 * (they aren't governed yet). Render a SHADOW pill instead
                 * with the discovered agentType (LLM_AGENT / MCP_SERVER /
                 * etc.) as the subtitle so the operator immediately knows
                 * what kind of un-enrolled agent surfaced. */
                r.kind === 'shadow' ? (
                  <div className="flex flex-col items-start gap-0.5">
                    <Pill tone="warning" size="sm">SHADOW</Pill>
                    {r.category && r.category !== '—' && (
                      <span className="text-[10px] text-v2-ink-muted">{r.category}</span>
                    )}
                  </div>
                ) : (
                  <Pill tone={r.archetype === 'ExteLet' ? 'trust' : r.archetype === 'AutoLet' ? 'cyan' : r.archetype === 'SysLet' ? 'muted' : 'success'} size="sm">
                    {ARCHETYPE_LABEL[r.archetype] || r.archetype}
                  </Pill>
                )
              )},
              /* Phase A · Source column — uniform discovery provenance across
               * governed + shadow rows. MANUAL renders muted (the default
               * "we authored it" state); engine sources render in tones
               * that match the dashboard convention so the operator can
               * eyeball where a row came from at a glance. */
              { key: 'discoverySource', header: 'Source', render: (r) => {
                const src = r.discoverySource || (r.kind === 'shadow' ? 'CORRELATION' : 'MANUAL');
                const label = DISCOVERY_SOURCE_LABEL[src] || src;
                const tone = src === 'MANUAL' ? 'muted'
                  : src === 'NETWORK' || src === 'MCP_PROTOCOL' || src === 'CLOUD_SCAN' ? 'cyan'
                  : src === 'CODE_REPO' || src === 'MARKETPLACE' ? 'trust'
                  : src === 'IDENTITY' || src === 'BEHAVIORAL' ? 'warning'
                  : src === 'CORRELATION' ? 'brand'
                  : 'muted';
                return (
                  <div className="flex flex-col items-start gap-0.5">
                    <Pill tone={tone} size="sm">{label}</Pill>
                    {/* Show discoveryConfidence only on shadow rows — for
                     * governed agents it's always 100 and just adds noise. */}
                    {r.kind === 'shadow' && typeof r.discoveryConfidence === 'number' && (
                      <span className="text-[10px] text-v2-ink-muted">
                        confidence {r.discoveryConfidence}%
                        {r.riskTier ? ` · risk ${r.riskTier.toLowerCase()}` : ''}
                      </span>
                    )}
                  </div>
                );
              }},
              { key: 'phase', header: 'Phase', render: (r) => (
                r.kind === 'shadow'
                  ? <Pill tone="muted" size="sm">{r.phase}</Pill>
                  : <Pill tone="cyan" size="sm">{r.phase}</Pill>
              ) },
              { key: 'trust', header: 'Trust', render: (r) => (
                r.kind === 'shadow'
                  /* R1.4 — Shadow rows are not yet compliance-scored. The
                   * previous "conf 80%" text read like a low trust score
                   * and confused operators. Show a muted "Not scored"
                   * pill instead; the discovery confidence is already
                   * surfaced under the Source column for shadow rows. */
                  ? <Pill tone="muted" size="sm">Not scored</Pill>
                  : <TrustChip score={r.trust} autonomy={r.autonomy} size="sm" />
              ) },
              /* R1.4 — Connectors column removed from the summary table
               * per user feedback. The Connectors info still surfaces in
               * the slideout right-rail and on the agent detail page. */
              { key: 'last', header: 'Last action', align: 'right', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.last}</span>
              )},
              /* G87 — per-row actions: Open in Canvas + Delete. Mirrors v1
                * AgentsPage row context menu. Open detail (full page) is
                * already wired via row click → slideout → Open detail. */
              { key: 'actions', header: '', align: 'right', render: (r) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/canvas?agentId=${r.id}`)}
                    title="Open this Agentlet's workflow graph in Canvas"
                  >
                    Canvas
                  </Button>
                  {/* DEF-FE-OPERATOR-CREATE-GATE-001 — also gate per-row
                      Delete on hasPermission('agent.delete'); previously
                      the button rendered for any role and only 403'd on
                      click. Keep the SysLet exception (always hide). */}
                  {r.archetype !== 'SysLet' && canDeleteAgent && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setDeleteTarget(r); setDeleteConfirmText(''); setDeleteError(null); }}
                      className="!text-v2-danger hover:!bg-v2-danger-soft"
                      title="Delete this Agentlet (a record is preserved in the audit trail)"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={openSlideout}
            density={density}
          />
        )}
      </div>

      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="agents"
          />
        </div>
      )}

      {/* Slideout for triage */}
      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} detail` : 'Agentlet detail'}
        width={520}
        tabs={[
          { id: 'summary', label: 'Summary' },
          { id: 'audit',   label: 'Audit Trail' },
          { id: 'verdict', label: 'Five-Layer Verdict' },
          { id: 'gates',   label: 'Phase Gates' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={selected.archetype === 'ExteLet' ? 'trust' : 'cyan'} size="sm">{ARCHETYPE_LABEL[selected.archetype] || selected.archetype}</Pill>
              <Pill tone="muted" size="sm">{selected.category}</Pill>
            </div>
            <div className="flex items-start gap-3">
              <div className={'w-10 h-10 rounded-v2-md flex items-center justify-center font-bold text-[14px] flex-shrink-0 ' + (
                selected.iconTone === 'cyan' ? 'bg-v2-cyan-soft text-v2-cyan' :
                selected.iconTone === 'brand' ? 'bg-v2-brand-soft text-v2-brand-strong' :
                selected.iconTone === 'success' ? 'bg-v2-success-soft text-v2-success' :
                selected.iconTone === 'warning' ? 'bg-v2-warning-soft text-v2-warning' :
                selected.iconTone === 'trust' ? 'bg-v2-trust-soft text-v2-trust' :
                'bg-v2-muted text-v2-ink-secondary'
              )}>{selected.iconLabel}</div>
              <div className="flex-1 min-w-0">
                <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name}</h2>
                <p className="text-[12px] text-v2-ink-secondary">{selected.archetype} · last action {selected.last}</p>
              </div>
              <TrustChip score={selected.trust} autonomy={selected.autonomy} />
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(isRebirthAgentletDetailEnabled() ? `/agents/${selected.id}` : '/agents')}
              >
                Open detail page
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  /* G189 — Run eval was a dead stub. Navigate to the agent's
                   * detail page Build tab where the eval-generator gate
                   * remediation already lives. We can't trigger a backend
                   * eval directly from the inventory slideout without first
                   * loading the full skill list and per-skill eval state. */
                  if (isRebirthAgentletDetailEnabled()) {
                    navigate(`/agents/${selected.id}#evals`);
                  } else {
                    navigate('/agents');
                  }
                }}
                title="Open the agent's Build tab to generate or run evals"
              >
                Run eval
              </Button>
              {/* Fix G5: Kill switch must be in the slideout header per §5.7 */}
              <KillSwitchButton
                agentName={selected.name}
                isActive={false}
                onConfirm={async (reason) => {
                  /* G228 — Kill switch is safety-critical: never claim
                   * "engaged" unless the backend actually engaged it.
                   * api.engageKillSwitch is currently a stub returning
                   * { ok:false, error:'...not implemented yet' }; earlier
                   * code unconditionally fired the red "engaged" toast,
                   * which is a lie that could lead operators to think
                   * traffic is blocked when it isn't. We now inspect
                   * res.ok and label the failure mode clearly. */
                  try {
                    const engage = api.engageKillSwitch;
                    if (typeof engage !== 'function') {
                      addToast({
                        tone: 'warning',
                        title: 'Kill switch NOT engaged — endpoint missing',
                        description: 'api.engageKillSwitch is not wired in this build. The agent continues to run. Contact platform admin.',
                      });
                      return;
                    }
                    const res = await engage(selected.id, reason);
                    if (res && res.ok === false) {
                      const stubbed = /not implemented/i.test(res.error || res.data?.error || '');
                      addToast({
                        tone: stubbed ? 'warning' : 'danger',
                        title: stubbed
                          ? 'Kill switch NOT engaged — backend endpoint pending'
                          : 'Kill switch FAILED',
                        description: stubbed
                          ? `${selected.name} continues to run. Backend kill-switch endpoint is not yet implemented; engage via platform-admin out-of-band procedures until it lands.`
                          : (res.error || res.data?.error || `Backend rejected the request (HTTP ${res?.status || '?'}). Agent is still running.`),
                      });
                      return;
                    }
                    addToast({ tone: 'danger', title: 'Kill switch engaged', description: `${selected.name} · ${reason}` });
                  } catch (e) {
                    addToast({ tone: 'danger', title: 'Kill switch FAILED — agent still running', description: e?.message || String(e) });
                  }
                }}
              />
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'summary' && (
          <div className="space-y-3 text-[12.5px] text-v2-ink-secondary leading-relaxed">
            <Card padding="sm">
              <Card.Header title="Quick stats · last 24h" />
              <Card.Body>
                {/* A freshly-created agent has no telemetry. Say "No runs
                  * yet" (not generic "No data"), and NEVER fabricate a block
                  * count — Blocks is a measured runtime signal, so deriving
                  * it from trust<80 invented a phantom "1 Blocks" on every
                  * fresh agent (trust seeds below 80). Show real counts when
                  * present; otherwise 0 once runs exist, "No runs yet" before. */}
                {(() => {
                  const exec = selected.executions24h ?? selected.tput;
                  const hasRuns = Number(exec ?? 0) > 0;
                  const NO_RUNS = 'No runs yet';
                  const blocks = selected.blocks24h;
                  return (
                    <div className="grid grid-cols-2 gap-3">
                      <Stat label="Executions" value={hasRuns ? exec : NO_RUNS} />
                      <Stat label="Approvals" value={selected.approvals24h ?? (hasRuns ? '0' : NO_RUNS)} />
                      <Stat
                        label="Blocks"
                        value={blocks ?? (hasRuns ? '0' : NO_RUNS)}
                        tone={Number(blocks ?? 0) > 0 ? 'danger' : 'ink'}
                      />
                      <Stat label="p95 latency" value={selected.p95LatencyMs ? `${selected.p95LatencyMs} ms` : NO_RUNS} />
                    </div>
                  );
                })()}
              </Card.Body>
            </Card>

            {/* What this agent's own runs have earned it. The counterpart to the design-time score
                on a discovered agent: same scale, same bands, different evidence. Unrated until it
                has run under governance, which the panel states rather than showing a zero. */}
            <RunTimeScorePanel agentId={selected.id} />

            <Card padding="sm">
              <Card.Header title="Connectors" />
              <Card.Body>
                <div className="flex flex-wrap gap-1.5">
                  {(selected.connectors || []).length === 0 && <span className="text-v2-ink-muted">No connectors assigned</span>}
                  {(selected.connectors || []).map((c) => (
                    <Pill key={c} tone="muted" size="sm">{c}</Pill>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </div>
        )}
        {selected && slideoutTab === 'audit' && (
          /* Fix G10: instead of a static "Phase 6 placeholder" string, we now
            * render the recent activity we actually know about for this row
            * (last action, last policy sync, kill-switch state) and link out
            * to the full Audit Trail filtered to this agent. This buys real
            * usefulness without waiting for the full Phase-6 audit feed. */
          <div className="space-y-3">
            <Card padding="sm">
              <Card.Header title="Recent activity" />
              <Card.Body>
                <ul className="space-y-2 text-[12.5px] text-v2-ink-secondary leading-relaxed">
                  <li className="flex items-baseline gap-2">
                    <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">{selected.last || '—'}</span>
                    <span>Last action recorded</span>
                  </li>
                  {selected.raw?.lastPolicySync && (
                    <li className="flex items-baseline gap-2">
                      <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">
                        {new Date(selected.raw.lastPolicySync).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span>Policy sync · hash <code className="font-mono-v2 text-[10.5px]">{(selected.raw.policyHash || '').slice(0, 10)}…</code></span>
                    </li>
                  )}
                  {selected.raw?.lastComplianceCheck && (
                    <li className="flex items-baseline gap-2">
                      <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">
                        {new Date(selected.raw.lastComplianceCheck).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span>Compliance check · score <span className="font-semibold text-v2-ink">{selected.trust}</span></span>
                    </li>
                  )}
                  {selected.raw?.phaseAdvancedAt && (
                    <li className="flex items-baseline gap-2">
                      <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">
                        {new Date(selected.raw.phaseAdvancedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span>Advanced into <Pill tone="cyan" size="sm">{selected.phase}</Pill></span>
                    </li>
                  )}
                </ul>
              </Card.Body>
            </Card>
            <Button variant="secondary" size="sm" onClick={() => navigate(`/workflow-audit?agentId=${encodeURIComponent(selected.id)}`)/* G436 */}>
              Open full audit trail →
            </Button>
            <p className="text-[11.5px] text-v2-ink-muted leading-relaxed">
              Full hash-chained event stream is available in <span className="font-semibold">Protection Center → Audit Trail</span>. Real-time event subscriptions here are coming soon.
            </p>
          </div>
        )}
        {selected && slideoutTab === 'verdict' && (
          <div className="space-y-3">
            <FiveLayerTrace
              layers={[
                { layer: 1, verdict: 'allow' },
                { layer: 2, verdict: selected.trust < 80 ? 'block' : 'allow' },
                { layer: 3, verdict: selected.trust < 80 ? 'skipped' : 'allow' },
                { layer: 4, verdict: selected.trust < 80 ? 'skipped' : 'allow' },
                { layer: 5, verdict: 'logged' },
              ]}
            />
            <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
              Last execution verdict trace. Per-layer decision drill-down is coming soon.
            </p>
          </div>
        )}
        {selected && slideoutTab === 'gates' && (
          <PhaseGate
            phase={(selected.phase || 'ACT').toUpperCase()}
            criteria={[
              { id: '1', passed: true,  label: '≥ 1 user or group assigned' },
              { id: '2', passed: true,  label: 'Valid gateway URL configured' },
              { id: '3', passed: true,  label: 'Agent status: active' },
              { id: '4', passed: selected.trust >= 80, label: 'All non-blocked skills published' },
              { id: '5', passed: selected.trust >= 80, label: 'No active kill switch', remediation: { label: 'Resolve' } },
              { id: '6', passed: true,  label: 'AgentLifecycleWorkflow reports healthy' },
            ]}
            advanceAction={null}
          />
        )}
      </Slideout>

      {/* Phase 5A — Native Agentlet creation moved from Modal → Slideout
         * after Phase 1A grew the form past modal-shape (5 sections,
         * 4 mode pickers, advisory + hint). The Slideout's sticky
         * footer + scrollable body matches the established V2 pattern
         * for substantial create flows (SkillCreateSlideout, Group /
         * Connector Group / Escalation Policy slideouts per G378). */}
      <AgentCreateSlideout
        isOpen={createOpen}
        onClose={closeCreate}
        archetype={createArchetype}
        form={createForm}
        setForm={setCreateForm}
        onSubmit={submitCreate}
        submitting={creating}
        onSaveDraft={saveDraft}
        savingDraft={savingDraft}
        draftId={currentDraftId}
      />

      {/* Saved server-side drafts (V331) — resume or discard an in-progress create form. */}
      <Slideout
        isOpen={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        ariaLabel="Saved Agentlet drafts"
        width={480}
        header={<div className="text-[15px] font-bold text-v2-ink">Saved drafts</div>}
      >
        <div className="p-4 space-y-2">
          {drafts.length === 0 ? (
            <p className="text-[13px] text-v2-ink-muted m-0">
              No saved drafts. Start a new Agentlet and choose <strong>Save draft</strong> to pick it up later — from any device.
            </p>
          ) : (
            drafts.map((d) => {
              const label = ARCHETYPE_LABEL[d.archetype === 'let' ? 'Let' : d.archetype === 'autolet' ? 'AutoLet' : d.archetype === 'syslet' ? 'SysLet' : 'ExteLet'] || d.archetype;
              const when = d.updatedAt ? new Date(d.updatedAt).toLocaleString() : '';
              return (
                <div key={d.id} className="flex items-center justify-between gap-3 rounded-v2-md border border-v2-border-subtle px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-v2-ink truncate">{d.name || 'Untitled draft'}</div>
                    <div className="text-[11px] text-v2-ink-muted">{label}{when ? ` · edited ${when}` : ''}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="secondary" size="sm" onClick={() => resumeDraft(d)}>Resume</Button>
                    <Button variant="ghost" size="sm" onClick={() => discardDraft(d.id)}>Discard</Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Slideout>

      {/* AutoLet — full multi-source pipeline (Runbook / Workflow / URL / Doc Library) */}
      <AutoCreateLauncher
        isOpen={autoLetOpen}
        seedFromDraft={autoletSeedDraft}
        onClose={() => { setAutoLetOpen(false); setAutoletSeedDraft(null); }}
        onCreated={() => {
          if (typeof loadAgents === 'function') loadAgents({ orgId: activeScope.orgId, tenantId: activeScope.tenantId });
        // Phase A — also refresh the unified inventory used by this page
        reloadInventory();
        }}
      />

      {/* EXTELET-V3 · Phase 6.1 — Manual entry point. The legacy
       * GelRegistrationLauncher single-screen modal is replaced by the
       * unified 6-step wizard. Opens BLANK (no seedFromDiscovery) so
       * the operator enters everything from scratch — same wizard, same
       * BE /onboard endpoint, same atomic register-plus-mint
       * transaction as the Discovery Promote path.
       *
       * Old GelRegistrationLauncher is preserved on /gel-agents for any
       * caller that explicitly hits that legacy route, but the canonical
       * "+ New ExteLet" path on /agents now uses the wizard. */}
      <ExteletRegistrationWizard
        isOpen={exteLetOpen}
        onClose={() => { setExteLetOpen(false); setExteletSeedDraft(null); }}
        seedFromDiscovery={null}
        seedFromDraft={exteletSeedDraft}
        onComplete={(envelope) => {
          setExteLetOpen(false);
          setExteletSeedDraft(null);
          if (typeof loadAgents === 'function') loadAgents({ orgId: activeScope.orgId, tenantId: activeScope.tenantId });
          reloadInventory();
          const agentId = envelope?.registration?.agentId;
          if (agentId) {
            navigate(`/agents/${agentId}?tab=registration`);
          }
        }}
      />

      {/* G87 — Delete confirmation modal. Mirrors AgentDetailPageV2's
        * type-to-confirm flow + impact summary; backend enforces lineage
        * cascade rules and we surface the result in plain language. */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        title={deleteTarget ? `Delete ${deleteTarget.name}?` : 'Delete Agentlet'}
        size="md"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button
              variant="danger"
              onClick={onDeleteConfirm}
              disabled={deleting || !deleteTarget || deleteConfirmText.trim() !== (deleteTarget?.name || '').trim()}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        )}
      >
        {deleteTarget && (
          <div className="space-y-3 text-[13px] text-v2-ink-secondary leading-relaxed">
            <p>
              This will remove <strong className="text-v2-ink">{deleteTarget.name}</strong> from the platform.
              Its workflow graph, skills, and assignments will be retired with it. A record of this deletion
              is preserved in the audit trail.
            </p>
            <div className="px-3 py-2 rounded-v2-sm bg-v2-warning-soft text-v2-warning text-[12px]">
              <strong>If this Agentlet has newer versions</strong> (v2, v3…), delete the latest version first.
              The platform will refuse the delete otherwise.
            </div>
            <label className="block text-[12px] font-semibold text-v2-ink mt-3">
              Type <code className="font-mono-v2 text-v2-ink">{deleteTarget.name}</code> to confirm:
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-v2-border-subtle rounded-v2-sm font-mono-v2 text-[13px]"
              placeholder={deleteTarget.name}
              autoFocus
            />
            {deleteError && (
              <div className="px-3 py-2 rounded-v2-sm bg-v2-danger-soft text-v2-danger text-[12px]">
                {deleteError}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Stat({ label, value, tone = 'ink' }) {
  const valueClass = tone === 'danger' ? 'text-v2-brand-strong' : 'text-v2-ink';
  return (
    <div>
      <div className="text-[10.5px] text-v2-ink-muted uppercase tracking-wider font-semibold">{label}</div>
      <div className={`text-[18px] font-extrabold ${valueClass} tabular-nums leading-tight mt-0.5`}>{value}</div>
    </div>
  );
}

export default function ResolvedAgentsInventoryPage(props) {
  if (isRebirthAgentletsInventoryEnabled()) return <AgentsInventoryPageV2Inner {...props} />;
  return <AgentsPage {...props} />;
}

export { AgentsInventoryPageV2Inner };
